import { useState, useCallback, useContext } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "react-toastify";

const useOrders = () => {
  const { api, userID } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [orderDetails, setOrderDetails] = useState({});
  const [deliveryPartners, setDeliveryPartners] = useState([]);
  const [catalogueStatus, setCatalogueStatus] = useState("");
  const [receiptData, setReceiptData] = useState({});
  const [summaryData, setSummaryData] = useState([]);

  const getNewOrders = useCallback(
    async (fromDate, toDate) => {
      try {
        setLoading(true);

        const res = await api.post(
          "/Whatsapp/getCatalogOrders",
          {
            userid: userID,
            secret: "string",
            fromdate: fromDate,
            todate: toDate,
            status: [
              "received",
              "paid",
              "accepted",
              "prepared",
              "driver_assigned",
              "delivery_picked",
              "dispatched",
              "assigned",
              "ready_for_takeaway",
              "ready_for_dinning",
            ],
          },
        );

        if (res.data?.responseCode === "00") {
          setOrders(res.data?.response || []);
          return res.data;
        }

        setOrders([]);
        // toast.error(res.data?.responseMessage || "Failed to fetch orders");
        return res.data;
      } catch (err) {
        console.error("getNewOrders error", err);

        setOrders([]);

        const errorMessage =
          err?.response?.data?.responseMessage ||
          err?.response?.data?.message ||
          err?.message ||
          "Error fetching orders";

        toast.error(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [api, userID],
  );

  const getOrderHistory = useCallback(
    async (fromDate, toDate) => {
      try {
        setLoading(true);

        const res = await api.post(
          "/Whatsapp/getCatalogOrders",
          {
            userid: userID,
            secret: "string",
            fromdate: fromDate,
            todate: toDate,
            status: [
              "completed",
              "dispatched",
              "delivered",
              "ready_for_dinning",
              "ready_for_takeaway",
              "not-delivered",
              "rejected",
              "driver_assigned",
              "driver_confirmed",
              "driver_pickup_arrival",
              "delivery_picked",
              "driver_dropoff_arrival",
            ],
          },
        );

        if (res.data?.responseCode === "00") {
          setOrderHistory(res.data?.response || []);
          return res.data;
        }

        setOrderHistory([]);
        // toast.error(res.data?.responseMessage || "Failed to fetch order history");
        return res.data;
      } catch (err) {
        console.error("getOrderHistory error", err);

        setOrderHistory([]);

        const errorMessage =
          err?.response?.data?.responseMessage ||
          err?.response?.data?.message ||
          err?.message ||
          "Error fetching order history";

        toast.error(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [api, userID],
  );

  //  Order Details
  const getOrderDetails = async (orderid) => {
    try {
      const res = await api.post(
        "/Order/v1.0/getOrderDetails",
        {
          orderid: orderid,
        },
      );

      if (res.data.responseCode === "00") {
        setOrderDetails(res.data.response);
        return res.data.response;
      }
    } catch (err) {
      console.error("getOrderDetails error", err);
    }
  };

  //  Change Order Status
  const updateOrderStatus = async (payload) => {
    try {
      const res = await api.post(
        "/Whatsapp/changeCatelogOrderStatus",
        payload,
      );

      return res.data;
    } catch (err) {
      console.error("updateOrderStatus error", err);

      return {
        responseCode: "99",
        responseMessage:
          err?.response?.data?.responseMessage ||
          err?.response?.data?.message ||
          err?.message,
        //  || "Order status update failed",
      };
    }
  };

  const sendReceiptAPI = async (payload) => {
    try {
      const res = await api.post(
        "/Whatsapp/sendReceipt",
        payload,
      );
      return res.data;
    } catch (err) {
      console.error("sendReceiptAPI error", err);
    }
  };

  //  Assign Delivery Partner
  const assignDeliveryPartner = async (orderid, deliveryPersonId) => {
    try {
      const res = await api.post(
        "/Order/assignDeliveryPerson",
        {
          userid: userID,
          orderid,
          deliveryPersonId,
        },
      );

      return res.data;
    } catch (err) {
      console.error("assignDeliveryPartner error", err);
    }
  };

  //  Get Delivery Partners
  const getDeliveryPartners = async () => {
    try {
      const res = await api.post(
        "/User/getDeliveryPersons",
        { userid: userID },
      );

      if (res.data.responseCode === "00") {
        setDeliveryPartners(res.data.response);
      }
    } catch (err) {
      console.error("getDeliveryPartners error", err);
    }
  };

  // ================= CATALOGUE STATUS =================

const getCatalogueStatus = useCallback(
  async (forceRefresh = false) => {
    if (!userID || !api) {
      return null;
    }

    const cacheKey = `catalogue_status_${userID}`;

    try {
      
      if (!forceRefresh) {
        const cachedStatus = sessionStorage.getItem(cacheKey);

        if (cachedStatus !== null) {
          setCatalogueStatus(cachedStatus);

          return {
            responseCode: "00",
            response: {
              catalogueStatus: cachedStatus,
            },
            fromCache: true,
          };
        }
      }

      const res = await api.post(
        "/Whatsapp/getCatalogueStatus",
        {
          userid: userID,
        },
      );

      if (res?.data?.responseCode === "00") {
        const latestStatus =
          res?.data?.response?.catalogueStatus ?? "";

        setCatalogueStatus(latestStatus);

        sessionStorage.setItem(
          cacheKey,
          String(latestStatus),
        );
      }

      return res.data;
    } catch (err) {
      console.error("getCatalogueStatus error", err);
      return null;
    }
  },
  [api, userID],
);

const updateCatalogueStatus = useCallback(
  async (status) => {
    if (!userID || !api) {
      return null;
    }

    const cacheKey = `catalogue_status_${userID}`;

    try {
      const res = await api.post(
        "/Whatsapp/changeCatalogueStatus",
        {
          userid: userID,
          secret: "string",
          status,
        },
      );

      if (res?.data?.responseCode === "00") {
        sessionStorage.removeItem(cacheKey);

        const latestResponse =
          await getCatalogueStatus(true);

        const latestStatus =
          latestResponse?.response?.catalogueStatus ??
          String(status || "");

        setCatalogueStatus(latestStatus);

        window.dispatchEvent(
          new CustomEvent(
            "catalogue-status-updated",
            {
              detail: {
                status: latestStatus,
              },
            },
          ),
        );

        return {
          ...res.data,
          latestStatus,
        };
      }

      return res.data;
    } catch (err) {
      console.error(
        "updateCatalogueStatus error",
        err,
      );

      return {
        responseCode: "99",
        responseMessage:
          err?.response?.data?.responseMessage ||
          err?.response?.data?.message ||
          err?.message ||
          "Catalogue status update failed",
      };
    }
  },
  [
    api,
    userID,
    getCatalogueStatus,
  ],
);

  //  Summary Report
  const getSummaryReport = async (fromDate, toDate) => {
    try {
      const res = await api.post(
        "/Order/v1.0/getOrderSummaryData",
        {
          userid: userID,
          fromDate,
          toDate,
        },
      );

      if (res.data.responseCode === "00") {
        setSummaryData(res.data.response);
      }
    } catch (err) {
      console.error("getSummaryReport error", err);
    }
  };

  const getBasicSummaryReport = async (fromDate, toDate) => {
    try {
      const res = await api.post(
        "/Order/getOrderSummaryDataAbhi",
        {
          userid: userID,
          fromDate,
          toDate,
        },
      );

      if (res.data?.responseCode === "00") {
        return res.data;
      }

      return res.data;
    } catch (err) {
      console.error("getBasicSummaryReport error", err);

      return {
        responseCode: "99",
        responseMessage:
          err?.response?.data?.responseMessage ||
          err?.response?.data?.message ||
          err?.message ||
          "Error fetching summary report",
        response: [],
        data2: {},
      };
    }
  };

  const getMergedSummaryReport = async (fromDate, toDate) => {
    try {
      const res = await api.post(
        "/Order/v1.0/getOrderSummaryData",
        {
          userid: userID,
          fromDate,
          toDate,
        },
      );

      if (res.data?.responseCode === "00") {
        return res.data;
      }

      return res.data;
    } catch (err) {
      console.error("getMergedSummaryReport error", err);

      return {
        responseCode: "99",
        responseMessage:
          err?.response?.data?.responseMessage ||
          err?.response?.data?.message ||
          err?.message ||
          "Error fetching merged summary report",
        response: [],
      };
    }
  };

  const getOrderDetailsSummary = async (orderid) => {
    try {
      const res = await api.post(
        "/Order/v1.0/getOrderDetails",
        {
          orderid: orderid,
          purpose: "print",
        },
      );

      if (res.data.responseCode === "00") {
        setOrderDetails(res.data.response);
        return res.data.response;
      }
    } catch (err) {
      console.error("getOrderDetails error", err);
    }
  };

  const notifyDriver = async ({ orderId, deliveryPartner }) => {
    try {
      const res = await api.post(
        "/Delivery/v1.0/notifyDriver",
        {
          userid: userID,
          orderId: String(orderId),
          deliveryPartner: deliveryPartner || "packky",
          orderStatus: "food is ready",
        },
      );

      return res.data;
    } catch (err) {
      console.error("notifyDriver error", err);
      return null;
    }
  };

  const updateScheduleOrder = async ({ userId, orderId1 }) => {
    try {
      const res = await api.post(
        "/Order/v1.0/updateScheduleOrder",
        null,
        {
          params: {
            userId,
            orderId1,
          },
        },
      );

      return res.data;
    } catch (err) {
      console.error("updateScheduleOrder error", err);

      return {
        responseCode: "99",
        responseMessage:
          err?.response?.data?.responseMessage ||
          err?.response?.data?.message ||
          err?.message ||
          "Schedule order update failed",
      };
    }
  };

  return {
    loading,
    orders,
    orderHistory,
    orderDetails,
    deliveryPartners,
    catalogueStatus,
    receiptData,
    summaryData,

    // actions
    getNewOrders,
    getOrderHistory,
    getOrderDetails,
    updateOrderStatus,
    assignDeliveryPartner,
    getDeliveryPartners,
    getCatalogueStatus,
    updateCatalogueStatus,
    getSummaryReport,
    sendReceiptAPI,
    getBasicSummaryReport,
    getMergedSummaryReport,
    getOrderDetailsSummary,
    notifyDriver,
    updateScheduleOrder,
  };
};

export default useOrders;
